import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { preloadRoute } from '@/lib/route-preload';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { allowedPaths } from '@/lib/auth';
import { SECTIONS as RULE_SECTIONS, rulePath, useRecsRules } from '@/lib/recs-rules';
import { INSIGHT_TABS, insightPath } from '@/lib/insight-tabs';
import { Tags, Coins, Filter, TrendingDown, TrendingUp, Image as ImageIcon, Rocket, RadioTower } from 'lucide-react';
import { PlatformLogo } from '@/components/commerce/platform-logo';
import { SETTINGS_TABS, ECOM_HIDE_TABS, settingsPath } from '@/lib/settings-tabs';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  BarChart3,
  Briefcase,
  Lightbulb,
  Calendar,
  PhoneCall,
  PhoneOutgoing,
  CalendarDays,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Cog,
  ContactRound,
  Sparkles,
  ShoppingBag,
  ShoppingCart,
  Package,
  Snowflake,
  FileText,
  MousePointerClick,
  Gauge,
  GraduationCap,
  Home,
  Handshake,
  LayoutGrid,
  LayoutTemplate,
  LifeBuoy,
  MailCheck,
  MailOpen,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  Radar,
  Radio,
  Receipt,
  Repeat,
  Send,
  Settings,
  Settings2,
  Target,
  Users,
  Webhook,
  Workflow,
  Zap,
  Trophy,
  ListTodo,
  Link2,
  Layers,
  Activity,
  Wand2,
  Compass,
  Route,
  Images,
  ShieldCheck,
  ListChecks,
  BadgeCheck,
  Wallet, ScanSearch, GitBranch, Map, Plug, Search, Store, Network} from 'lucide-react';
import { useWhatsAppThreads, useScheduling, useStudents, useEcommerce, useLeadDesk, useDeals } from '@/hooks/use-settings';
import { usePartialCaptureCount } from '@/hooks/use-partial-captures';
import { countUnread, READ_CHANGE_EVENT } from '@/lib/wa-read';
import { inMemberSession, setCurrentWorkspaceName } from '@/lib/agencyMember';
import { cn } from '@/lib/utils';
import { useReportsV2 } from '@/lib/reports-v2';
import { EXPLORERS } from '@/lib/report-index';
import { openFindReport } from '@/components/reports/find-report';
import { Badge } from '@/components/ui/badge';
import { MMLogoMark } from '@/components/brand/mm-logo';
import { CookieBeesWordmark } from '@/components/brand/cookiebees-wordmark';
import { useWorkspaceFocus } from '@/hooks/use-hosting-only';
import { ADD_FEATURES_PATH, openInFocus } from '@/lib/workspace-modules';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Nav items hidden when this workspace turns scheduling off (lead-gen mode).
const SCHEDULING_PATHS = new Set(['/calendar', '/meetings']);
// The Academy group - hidden when this workspace turns the students module off.
const STUDENT_PATHS = new Set(['/students', '/cohorts', '/finance', '/expenses']);
// THE LEAD DESK, hidden from a pure store and restored for a store that also
// works enquiries.
//
// Not every business is one or the other. A dealer-led store takes a WhatsApp
// enquiry, a rep works it, and the sale is keyed in as a draft order weeks
// later - one funnel with one person in the middle, which the either/or
// onboarding made unbuildable. These pages were never restricted on the
// server: leads, forms and chat live in the same database and the same org as
// the orders. Only this list pretended otherwise, so restoring them is a
// question of navigation rather than architecture.
const LEAD_DESK = new Set(['/leads', '/pipelines', '/contacts', '/calling-lists', '/cold-captures', '/follow-ups', '/tasks',
  '/forms', '/scoring', '/popups', '/chat', '/automation/sequences',
  // Lead-era measurement: superseded by the order drawer for a pure store, and
  // the whole point for a store whose enquiries arrive before the order.
  // Lead attribution also carries what Analytics had (leads funnel, by
  // source, lead ROAS): dead furniture in a pure store, and the report a
  // hybrid opens first.
  '/tracking/conversions', '/tracking/attribution', '/reports/leads']);

// Never relevant to a store, hybrid or not: the appointment and academy
// transactional set, and a template page whose send action the store builder
// does not offer. Calendar and Meetings are absent on purpose - they answer to
// the scheduling flag, so a hybrid that books calls still gets them.
const STORE_NEVER = new Set(['/calendar', '/meetings', '/automation/email-templates',
  '/automation/workflow-emails']);

// Does this workspace have this page at all? The ONE answer, shared with the
// team access picker (pages/settings/team-tab) so a store is offered its
// store's pages and a lead desk its lead pages, never the other's.
export function inWorkspace(i, { schedulingOn, studentsOn, ecomOn, leadsOn, dealsOn, upsellsOn = true }) {
  return (schedulingOn || !SCHEDULING_PATHS.has(i.to)) &&
    // Upsells & bumps: only where order lines say what they are (SamCart).
    (upsellsOn || !i.upsellsOnly) &&
    (studentsOn || !STUDENT_PATHS.has(i.to)) &&
    (ecomOn || !i.ecomOnly) &&
    (!ecomOn || !i.leadOnly) &&
    (dealsOn || !i.dealsOnly) &&
    // A store hides the lead desk unless it is also working enquiries.
    (!ecomOn || leadsOn || !LEAD_DESK.has(i.to)) &&
    (!ecomOn || !STORE_NEVER.has(i.to));
}

// Always visible, ungrouped.
const PINNED = [{ to: '/dashboard', label: 'Dashboard', icon: Home }];

/**
 * "Setup 3 of 5" until every required step is proved (api services/setupProgress), so
 * a workspace that left halfway always sees the way back (owner, 16 Oct 2026:
 * "so clients who start dont drop off mid way"). It opens the guided setup
 * (pages/setup). Gone the moment setup is complete; never shown while it is
 * loading.
 */
function SetupNudge() {
  const { data } = useQuery({
    queryKey: ['setup-progress'],
    queryFn: async () => (await api.get('/api/onboarding/progress')).data,
    // A nudge, on every page: if it cannot load it stays away quietly rather
    // than put "part of this page could not load" on whatever page is open.
    staleTime: 5 * 60_000, retry: false, meta: { silent: true },
  });
  // nudge: false for a store live for months on the earlier five steps: the
  // Shopify journey's new steps wait on the setup page, not as a new badge.
  if (!data || data.complete || data.nudge === false) return null;
  const pct = Math.round((100 * data.done) / Math.max(1, data.required));
  return (
    <Link to="/setup" className="mb-2 block rounded-md border border-primary/30 bg-primary/[0.06] px-2.5 py-2 text-[13px] transition-colors hover:border-primary/60">
      <span className="flex items-center justify-between font-semibold text-primary">
        Setup <span className="tabular font-medium">{data.done} of {data.required}</span>
      </span>
      <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-primary/15">
        <span className="block h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </span>
    </Link>
  );
}

// Server GTM wears Tag Manager's own mark in the rail, like the page does.
const GtmMark = ({ className }) => <PlatformLogo platform="sgtm" title="Server GTM" className={className} />;

// Two things a store pays for and could not find (owner, 15 Oct 2026: "Server
// GTM container section and POAS section is buried deep"): each is a row of
// its own in the tracking group, the server container with its live state.
const SERVER_GTM_ITEM = { to: '/server-container', label: 'Server GTM', icon: GtmMark, statusKey: 'sgtm' };
// A focused workspace's cards (lib/workspace-modules), pinned under Server GTM.
const ADD_FEATURES_ITEM = { to: ADD_FEATURES_PATH, label: 'Add features', icon: Sparkles };
const PROFIT_ITEM = { to: '/studio?tab=value', label: 'Profit & POAS', icon: Coins, gate: '/studio', ecomOnly: true };
// What buyers add at checkout and after it (pages/commerce-upsells), where the
// order lines say so (owner, 16 Oct 2026).
const UPSELLS_ITEM = { to: '/commerce/upsells', label: 'Upsells & bumps', icon: TrendingUp, gate: '/commerce/insights', ecomOnly: true, upsellsOnly: true };

// Parent groups, each collapsible. `tag` ('inbound' | 'outbound') shows a
// direction chip - only used in Messaging, where a new user can't otherwise
// tell which tools receive vs which send. `gate` overrides the permission
// path when an item rides a parent's permission (Broadcasts is gated by the
// /whatsapp module, so it inherits inbox access).
// Exported so the nav-registry test can assert every item resolves to a real
// module. Event stream shipped invisible to everyone because it never did.
export const NAV_GROUPS = [
  // SALES SITS DIRECTLY UNDER THE COMMAND CENTER (owner, 1 Nov 2026: "i feel
  // this data is buried somewhere inside just like orders is in the 4th
  // section"). Orders and revenue are what a store owner opens first, so
  // the list of sales is the second door after the Command Center.
  {
    key: 'sales', label: 'Sales', icon: Briefcase,
    items: [
      { to: '/commerce', label: 'Orders', icon: ShoppingBag, gate: '/commerce/orders', ecomOnly: true },
      UPSELLS_ITEM,
      { to: '/commerce/customers', label: 'Customers', icon: ContactRound, gate: '/commerce/customers', ecomOnly: true },
      // Sales > Actions retired 24 Sep 2026: a second "Actions" beside Growth >
      // Today's Actions, built on an assumed 55% margin and modeled spend (the
      // cost-state canon forbids both), 6-35s cold. /commerce/actions redirects.
      // Sits before Recovery on purpose: the list of people who left is the
      // thing you look at, and Recovery is what you then do about it.
      { to: '/commerce/abandoned', label: 'Abandoned checkouts', icon: ShoppingCart, gate: '/commerce/abandoned', ecomOnly: true },
      { to: '/commerce/recovery', label: 'Recovery', icon: Sparkles, gate: '/commerce/recovery', ecomOnly: true },
      // Inside Recommendations the section opens like Measurement does: the
      // rules as a sub-menu with a live count, one row per rule. The rows
      // come from the same list the pages read (lib/recs-rules).
      { to: '/commerce/recommendations', label: 'Recommendations', icon: Wand2, gate: '/commerce/recommendations', ecomOnly: true,
        children: [
          { to: '/commerce/recommendations/rules', label: 'Recommendation rules', gate: '/commerce/recommendations', captionKey: 'recsLive',
            // desc + icon ride along for the panel. Both already exist on the
            // rule - the rail never had room for them and so never asked.
            children: RULE_SECTIONS.map((r) => ({ to: rulePath(r.key), label: r.title, n: r.n, liveKey: r.key,
              desc: r.why, icon: r.icon, gate: '/commerce/recommendations' })) },
        ] },
      { to: '/commerce/costs', label: 'Costs', icon: Wallet, gate: '/commerce/costs', ecomOnly: true },
      { to: '/leads', label: 'Pipeline', icon: LayoutGrid },
      // Same permission as its route (App.js gates /pipelines on /leads); without
      // the gate it resolved to no module and was hidden from saved-permission users.
      { to: '/pipelines', label: 'Pipeline setup', icon: GitBranch, gate: '/leads' },
      // Deals sit next to Pipeline because they are the same work seen the
      // other way round: Pipeline is one card per person, Deals is one per
      // sale. Hidden until a workspace opts in - nothing about the product
      // changes for anyone who has not.
      { to: '/deals', label: 'Deals', icon: Handshake, dealsOnly: true },
      { to: '/offerings', label: 'Products & services', icon: Package, dealsOnly: true },
      { to: '/contacts', label: 'Contacts', icon: ContactRound },
      { to: '/calling-lists', label: 'Calling lists', icon: PhoneOutgoing, gate: '/contacts' },
      { to: '/cold-captures', label: 'Cold Captures', icon: Snowflake, badgeKey: 'coldCaptures' },
      { to: '/follow-ups', label: 'Follow-ups', icon: Clock },
      { to: '/tasks', label: 'Tasks', icon: ListTodo },
      { to: '/calendar', label: 'Calendar', icon: CalendarDays },
      { to: '/meetings', label: 'Meetings', icon: Calendar },
      // Calls in and out, with the source each inbound call came from. Stores
      // take phone orders too, so it is not part of the lead desk.
      { to: '/calls', label: 'Calls', icon: PhoneCall },
    ],
  },
  {
    // The Growth layer follows Sales: the client-shaped truth is that reports
    // scattered across sections read as disconnected - the Command Center and
    // the Action Feed are the front door, everything else is the evidence
    // behind their "Why?".
    key: 'growth', label: 'Growth', icon: Gauge,
    items: [
      // First in the group on purpose: it is the answer to "where do I start",
      // which is the question every new user and every demo opens with.
      { to: '/growth/guide', label: 'Where to look', icon: Map, gate: '/growth/guide', ecomOnly: true },
      { to: '/growth/actions', label: "Today's Actions", icon: Compass, gate: '/growth/actions', ecomOnly: true },
      { to: '/growth/findings', label: 'What We Found', icon: Lightbulb, gate: '/growth/findings', ecomOnly: true },
      { to: '/growth/customer-value', label: 'Customer Value', icon: ContactRound, gate: '/growth/customer-value', ecomOnly: true },
      { to: '/growth/buyers', label: 'Who Bought', icon: ContactRound, gate: '/growth/buyers', ecomOnly: true },
      // THREE PAGES SAID "AUDIENCE" AND MEANT THREE DIFFERENT THINGS.
      //
      // Nobody could tell them apart, including the people who built them, so
      // each is now named for what it is rather than what it is about:
      //   what you already buy, and what it costs   -> Audience performance
      //   turning site behaviour into an ad audience -> Build an audience
      //   groups of known customers you can message  -> Customer segments
      // The word "audience" survives only where the thing really is an ad
      // audience; the customer-list one no longer competes for it.
      // The three lenses are the same money read at three depths, and the page
      // says so at the top - so they are CHILDREN, shown only while you stand
      // inside the page, rather than three permanent rows in an already long
      // rail. The level lives in the URL, so each is a real, shareable address.
      // ONE home for every campaign question (services/campaignsHub on the
      // daily facts). Audience performance became this page on 17 Sep 2026;
      // its levels are the same money at four depths.
      { to: '/growth/campaigns', label: 'Campaigns', icon: Target, gate: '/growth/campaigns', ecomOnly: true,
        children: [
          { to: '/growth/campaigns?level=platform', label: 'Platforms', icon: LayoutGrid, desc: 'Spend and return, one row per platform', gate: '/growth/campaigns' },
          { to: '/growth/campaigns?level=campaign', label: 'Campaigns', icon: Target, desc: 'Every campaign, ranked by what it returned', gate: '/growth/campaigns' },
          { to: '/growth/campaigns?level=adset', label: 'Ad sets', icon: Layers, desc: 'The audiences inside a campaign', gate: '/growth/campaigns' },
          { to: '/growth/campaigns?level=ad', label: 'Ads', icon: ImageIcon, desc: 'The creatives inside an ad set', gate: '/growth/campaigns' },
        ] },
      // Its questions are campaign questions, so it sits under Campaigns
      // (moved from Signal Studio 24 Sep 2026).
      { to: '/growth/leaks', label: 'Where you lose money', icon: AlertTriangle, gate: '/growth/leaks', ecomOnly: true },
      { to: '/growth/channel-roles', label: 'Channel Roles', icon: Route, gate: '/growth/channel-roles', ecomOnly: true },
      // Profit & Economics retired 24 Sep 2026 (/growth/economics redirects to
      // Insights > Product, where product profitability now sits).
      { to: '/growth/funnels', label: 'Funnels', icon: Zap, gate: '/growth/funnels', ecomOnly: true,
        // Four views that used to be a chooser column INSIDE the page, eating
        // a third of the width the tables needed. Same pattern as Audience
        // performance: the views are the page's sub-menu, the page is the view.
        children: [
          { to: '/growth/funnels?view=store', label: 'The store funnel', icon: Filter, desc: 'Visit to order, and where people drop out', gate: '/growth/funnels' },
          { to: '/growth/funnels?view=leaks', label: 'The price of each leak', icon: TrendingDown, desc: 'What each drop-off costs in sales', gate: '/growth/funnels' },
          { to: '/growth/funnels?view=product', label: 'By product', icon: Package, desc: 'The same funnel, one product at a time', gate: '/growth/funnels' },
        ] },
      { to: '/growth/creatives', label: 'Creatives', icon: Images, gate: '/growth/creatives', ecomOnly: true },
    ],
  },
  {
    // SIGNAL STUDIO. Six pages that all answer one question - what are we
    // telling the ad platforms - used to sit in three different groups, so
    // nobody could see them as one thing and an agency had no way to browse
    // what we can do. The order is the order you use them in: what exists,
    // what is switched on, where it goes, did it arrive. (Where you lose money
    // moved under Campaigns on 24 Sep 2026: its questions are campaign ones.)
    key: 'studio', label: 'Signal Studio', icon: Radio,
    items: [
      { to: '/studio', label: 'Signal catalogue', icon: Layers, gate: '/studio', ecomOnly: true,
        children: [
          // The Overview, then the six steps of one path in their order
          // (27 Oct 2026). The old addresses (setup, platforms, checklist,
          // value) still open inside these.
          { to: '/studio?tab=overview', label: 'Overview', icon: Compass, desc: 'Everything the catalogue sends, and your path', gate: '/studio' },
          { to: '/studio?tab=connect', label: '1. Connect', icon: RadioTower, desc: 'Each ad platform, and how Cookiebees works beside your tags', gate: '/studio' },
          { to: '/studio?tab=events', label: '2. Events', icon: Layers, desc: 'Which events each platform receives, and their names', gate: '/studio' },
          { to: '/studio?tab=params', label: '3. Event data', icon: Tags, desc: 'What every event carries, and what each sale is worth', gate: '/studio' },
          { to: '/studio?tab=goals', label: '4. Goals', icon: Lightbulb, desc: 'Extra conversions for the sales you want more of', gate: '/studio' },
          { to: '/studio?tab=audiences', label: '5. Audiences', icon: Users, desc: 'Who to show ads to, and who to keep out', gate: '/studio' },
          { to: '/studio?tab=ads', label: '6. Use in ads', icon: Target, desc: 'What your ads optimise for, what Meta reports, and the checks', gate: '/studio' },
        ] },
      PROFIT_ITEM,
      SERVER_GTM_ITEM,
      // A lead desk's one part of the catalogue: the standard-events matrix.
      { to: '/studio?tab=events', label: 'Standard events', icon: Layers, gate: '/tracking/destinations', leadOnly: true },
      { to: '/tracking/stream', label: 'Event stream', icon: Activity },
      { to: '/signal-health', label: 'Signal Health', icon: ShieldCheck, gate: '/signal-health', ecomOnly: true },
      { to: '/number-check', label: 'Number check', icon: ListChecks, gate: '/number-check', ecomOnly: true },
      { to: '/value-report', label: 'What Cookiebees added', icon: BadgeCheck, gate: '/value-report', ecomOnly: true },
      { to: '/sale-season', label: 'Sale season', icon: CalendarDays, gate: '/sale-season', ecomOnly: true },
      { to: '/tracking/health', label: 'Alerts & diagnostics', icon: LifeBuoy },
      { to: '/tracking/conversions', label: 'Conversions', icon: Trophy },
    ],
  },
  {
    key: 'measurement', label: 'Measurement', icon: Gauge,
    items: [
      { to: '/commerce/attribution', label: 'Attribution', icon: Radar, gate: '/commerce/attribution', ecomOnly: true },
      // A lead desk reads Repeat ad clicks as a tab of Lead attribution; a
      // pure store hides that page, so the store gets its own row.
      { to: '/tracking/repeat-clicks', label: 'Repeat ad clicks', icon: MousePointerClick, gate: '/commerce/attribution', ecomOnly: true },
      { to: '/commerce/insights', label: 'Analytics & Insights', icon: BarChart3, gate: '/commerce/insights', ecomOnly: true,
        // Each report is its own page under the section, like the tracking pages.
        children: INSIGHT_TABS.map((t) => ({ to: insightPath(t.v), label: t.l, desc: t.d, icon: t.Icon, gate: '/commerce/insights' })) },
      { to: '/commerce/segments', label: 'Customer segments', icon: Layers, gate: '/commerce/segments', ecomOnly: true },
      // Every customer list on every ad platform and account, with its health.
      { to: '/commerce/audiences', label: 'Audience lists', icon: Target, gate: '/commerce/segments', ecomOnly: true },
      { to: '/tracking/attribution', label: 'Lead attribution', icon: Zap },
      { to: '/tracking/email', label: 'Email analytics', icon: MailOpen },
      { to: '/tracking/site-scan', label: 'Site scan', icon: ScanSearch, gate: '/tracking/site-scan', ecomOnly: true },
      { to: '/tracking/webhooks', label: 'Webhooks', icon: Webhook },
      { to: '/tracking/links', label: 'Links', icon: Link2 },
    ],
  },
  {
    key: 'messaging', label: 'Messaging', icon: MessagesSquare,
    items: [
      { to: '/whatsapp', label: 'WA Inbox', icon: MessageCircle, badgeKey: 'whatsapp', tag: 'inbound' },
      { to: '/whatsapp/broadcasts', label: 'WA Broadcast', icon: Megaphone, gate: '/whatsapp', tag: 'outbound' },
      // Every WhatsApp setting (number, templates, automatic replies, catalog,
      // website widget, cost) - the same page Integrations opens for WhatsApp.
      // Beside the inbox and broadcasts it powers; rides Settings' permission.
      { to: '/integrations/whatsapp', label: 'WA Setup', icon: Settings2, gate: '/settings' },
    ],
  },
  {
    key: 'templates', label: 'Templates', icon: LayoutTemplate,
    items: [
      { to: '/automation/whatsapp-templates', label: 'WhatsApp templates', icon: MessageCircle },
      { to: '/automation/whatsapp-flows', label: 'WhatsApp flows', icon: Workflow },
      { to: '/automation/workflow-emails', label: 'Workflow emails', icon: Send },
      // The recovery emails are templates like any other, so they live with the
      // rest of them rather than buried inside the Recovery config page.
      { to: '/commerce/recovery/email', label: 'Store emails', icon: MailCheck, gate: '/commerce/recovery/email', ecomOnly: true },
      { to: '/automation/email-templates', label: 'System emails', icon: MailCheck },
    ],
  },
  {
    key: 'automation', label: 'Automation', icon: Cog,
    items: [
      { to: '/automations', label: 'Workflows', icon: Workflow },
      { to: '/scoring', label: 'Lead scoring', icon: Target },
      { to: '/forms', label: 'Forms', icon: FileText },
      { to: '/popups', label: 'Popups', icon: MousePointerClick },
      { to: '/chat', label: 'Website chat', icon: MessagesSquare },
      { to: '/automation/sequences', label: 'Sequences', icon: Repeat },
    ],
  },
  {
    key: 'academy', label: 'Academy', icon: GraduationCap,
    items: [
      { to: '/students', label: 'Students', icon: GraduationCap },
      { to: '/cohorts', label: 'Cohorts', icon: Users },
      { to: '/finance', label: 'Finance', icon: Receipt },
      { to: '/expenses', label: 'Expenses', icon: Banknote },
    ],
  },
  {
    key: 'workspace', label: 'Workspace', icon: Settings,
    items: [
      // Every settings page as the sub-menu. The settings page hides its own
      // tab strip on desktop and relies on the sidebar to list them; each tab
      // is a real address (?tab=...). Team is one of these tabs, so it is no
      // longer a row of its own - it keeps its own permission gate.
      // Every connector on one page, with whether it is working. Beside
      // Settings because most of them are still configured there. Anyone who
      // could open Settings or Destinations can open it: the ad-platform cards
      // Destinations held are here now, so a Destinations grant must reach them.
      { to: '/integrations', label: 'Integrations', icon: Plug, gate: ['/settings', '/tracking/destinations'] },
      // The guided setup, always reachable once the nudge above has gone.
      { to: '/setup', label: 'Setup', icon: Rocket, gate: ['/setup', '/settings'] },
      { to: '/settings', label: 'Settings', icon: Settings,
        children: SETTINGS_TABS.map((t) => ({
          to: settingsPath(t.value), label: t.label, icon: t.icon, desc: t.desc,
          gate: t.gate || '/settings',
          ecomOnly: t.ecomOnly, ecomHide: ECOM_HIDE_TABS.has(t.value),
        })) },
    ],
  },
];

// ── The new reports' menu (reports plan, option A: by question) ─────────────
//
// Four report sections, each answering one kind of question: Home (what should
// I do?), Performance (where did it come from, and why?), Store (show me the
// actual orders and people) and Tracking (can I believe these numbers?). The
// rest of the product is unchanged. Shown only while the new reports are on
// (lib/reports-v2); every leaf reuses the gate its page has today, so a role
// that could open a report still can and one that could not still cannot.
const explorerChildren = (key) => (EXPLORERS[key]?.tabs || []).map((t) => ({
  to: `/reports/${key}/${t.v}`, label: t.l, desc: t.q, icon: t.Icon, gate: EXPLORERS[key].gate,
}));
export const NAV_GROUPS_V2 = [
  {
    key: 'home', label: 'Home', icon: Home,
    items: [
      // The front door: the Command Center for a store, the lead desk's dashboard otherwise.
      { to: '/dashboard', label: 'Dashboard', ecomLabel: 'Command Center', icon: Home, ecomIcon: Gauge, gate: '/dashboard' },
      { to: '/growth/actions', label: "Today's Actions", icon: Compass, gate: '/growth/actions', ecomOnly: true },
      { to: '/reports/found', label: 'What We Found', icon: Lightbulb, gate: '/growth/findings', ecomOnly: true,
        children: explorerChildren('found') },
      { to: '/sale-season', label: 'Sale season', icon: CalendarDays, gate: '/sale-season', ecomOnly: true },
      { to: '/tracking/conversions', label: 'Conversions', icon: Trophy },
    ],
  },
  {
    key: 'store', label: 'Store', icon: Store,
    items: [
      { to: '/commerce', label: 'Orders', icon: ShoppingBag, gate: '/commerce/orders', ecomOnly: true },
      UPSELLS_ITEM,
      { to: '/commerce/customers', label: 'Customers', icon: Users, gate: '/commerce/customers', ecomOnly: true },
      { to: '/commerce/abandoned', label: 'Abandoned checkouts', icon: ShoppingCart, gate: '/commerce/abandoned', ecomOnly: true },
      { to: '/commerce/recovery', label: 'Recovery', icon: Sparkles, gate: '/commerce/recovery', ecomOnly: true },
      // The recovery emails are what Recovery sends, so they sit beside it.
      { to: '/commerce/recovery/email', label: 'Store emails', icon: MailCheck, gate: '/commerce/recovery/email', ecomOnly: true },
      { to: '/commerce/recommendations', label: 'Recommendations', icon: Wand2, gate: '/commerce/recommendations', ecomOnly: true,
        children: [
          { to: '/commerce/recommendations/rules', label: 'Recommendation rules', gate: '/commerce/recommendations', captionKey: 'recsLive',
            children: RULE_SECTIONS.map((r) => ({ to: rulePath(r.key), label: r.title, n: r.n, liveKey: r.key,
              desc: r.why, icon: r.icon, gate: '/commerce/recommendations' })) },
        ] },
      { to: '/commerce/costs', label: 'Costs', icon: Wallet, gate: '/commerce/costs', ecomOnly: true },
    ],
  },
  {
    key: 'performance', label: 'Performance', icon: BarChart3,
    items: [
      { to: '/reports/marketing', label: 'Marketing', icon: Megaphone, gate: '/growth/campaigns', ecomOnly: true, children: explorerChildren('marketing') },
      { to: '/reports/channels', label: 'Channels', icon: Network, gate: '/commerce/attribution', ecomOnly: true, children: explorerChildren('channels') },
      { to: '/reports/products', label: 'Products', icon: Package, gate: '/commerce/insights', ecomOnly: true, children: explorerChildren('products') },
      { to: '/reports/customers', label: 'Customers', icon: ContactRound, gate: '/growth/customer-value', ecomOnly: true, children: explorerChildren('customers') },
      { to: '/reports/leads', label: 'Leads', icon: Zap, gate: '/tracking/attribution', children: explorerChildren('leads') },
    ],
  },
  {
    // The lead desk's working lists. Not reports: the people and deals you act on.
    key: 'crm', label: 'CRM', icon: Briefcase,
    items: [
      { to: '/leads', label: 'Pipeline', icon: LayoutGrid },
      { to: '/pipelines', label: 'Pipeline setup', icon: GitBranch, gate: '/leads' },
      { to: '/deals', label: 'Deals', icon: Handshake, dealsOnly: true },
      { to: '/offerings', label: 'Products & services', icon: Package, dealsOnly: true },
      { to: '/contacts', label: 'Contacts', icon: ContactRound },
      { to: '/calling-lists', label: 'Calling lists', icon: PhoneOutgoing, gate: '/contacts' },
      { to: '/cold-captures', label: 'Cold Captures', icon: Snowflake, badgeKey: 'coldCaptures' },
      { to: '/follow-ups', label: 'Follow-ups', icon: Clock },
      { to: '/tasks', label: 'Tasks', icon: ListTodo },
      { to: '/calendar', label: 'Calendar', icon: CalendarDays },
      { to: '/meetings', label: 'Meetings', icon: Calendar },
    ],
  },
  {
    key: 'tracking', label: 'Tracking', icon: ShieldCheck,
    items: [
      { to: '/signal-health', label: 'Signal Health', icon: ShieldCheck, gate: '/signal-health', ecomOnly: true },
      { to: '/number-check', label: 'Number check', icon: ListChecks, gate: '/number-check', ecomOnly: true },
      { to: '/value-report', label: 'What Cookiebees added', icon: BadgeCheck, gate: '/value-report', ecomOnly: true },
      { to: '/studio', label: 'Signal Studio', icon: Layers, gate: '/studio', ecomOnly: true,
        children: NAV_GROUPS.find((g) => g.key === 'studio').items.find((i) => i.to === '/studio').children },
      PROFIT_ITEM,
      SERVER_GTM_ITEM,
      { to: '/studio?tab=events', label: 'Standard events', icon: Layers, gate: '/tracking/destinations', leadOnly: true },
      { to: '/tracking/stream', label: 'Event stream', icon: Activity },
      { to: '/tracking/health', label: 'Alerts & diagnostics', icon: LifeBuoy },
      { to: '/tracking/repeat-clicks', label: 'Repeat ad clicks', icon: MousePointerClick, gate: ['/commerce/attribution', '/tracking/attribution'] },
      { to: '/tracking/site-scan', label: 'Site scan', icon: ScanSearch, gate: '/tracking/site-scan', ecomOnly: true },
      { to: '/tracking/webhooks', label: 'Webhooks', icon: Webhook },
      { to: '/tracking/links', label: 'Links', icon: Link2 },
    ],
  },
  {
    key: 'messaging', label: 'Messaging', icon: MessagesSquare,
    items: [
      ...NAV_GROUPS.find((g) => g.key === 'messaging').items,
      { to: '/tracking/email', label: 'Email analytics', icon: MailOpen },
      { to: '/calls', label: 'Calls', icon: PhoneCall },
    ],
  },
  {
    key: 'templates', label: 'Templates', icon: LayoutTemplate,
    items: NAV_GROUPS.find((g) => g.key === 'templates').items.filter((i) => i.to !== '/commerce/recovery/email'),
  },
  ...NAV_GROUPS.filter((g) => ['automation', 'academy', 'workspace'].includes(g.key)),
];

// Groups deep enough to be their OWN place. Clicking one hides the rest of the
// nav and shows only that section, with a way back - 30-odd rows visible at
// once is a menu you scan, not one you read, and Measurement alone is 14.
//
// Deliberately not every group: entering a section to reach a single Automation
// item is a context switch bought for nothing. Small groups keep the accordion,
// which costs nothing when there is little to hide.
// The drill-in experiment (sections as doors, back button inside) is retired
// on the user's verdict: "users click and they just see 5 options with a back
// button - i did not like it". Every section now shows its pages inline.


const GROUP_STATE_KEY = 'mm-nav-groups';
// First-ever load (before the user has toggled anything): open the two daily
// groups. After that we remember their last open/closed state per browser.
const DEFAULT_OPEN = { growth: true, sales: true, measurement: true, messaging: true, templates: true, automation: true, workspace: true, academy: true,
  home: true, performance: true, store: true, crm: true, tracking: true };

function loadGroupState() {
  try {
    const raw = localStorage.getItem(GROUP_STATE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

// The single active leaf = the longest matching path, so /whatsapp/broadcasts
// wins over /whatsapp and /leads/archive maps back to /leads.
// Every page, including the sub-menu pages under a section page.
export function flatItems(items) {
  return items.flatMap((i) => [i, ...(i.children ? flatItems(i.children) : [])]);
}
// Standing inside a page = on it or on one of its children.
const inTrail = (item, activeTo) => !!activeTo && (activeTo === item.to || activeTo.startsWith(`${item.to}/`));

// A sub-menu row can be a QUERY on the parent's page ("?level=platform"), not a
// path of its own. resolveActive only ever sees the pathname, so without this
// none of those rows would ever highlight and the reader could not tell which
// lens they were looking at from the rail.
const DEFAULT_QUERY = { '/growth/funnels': 'view=store', '/settings': 'tab=general' };
// `also`: more queries the same row answers for. A settings group is one row
// for several pages (?tab=general, ?tab=team ...), and must stay lit on each.
function queryChildActive(to, pathname, search, also = []) {
  const [path, query] = String(to).split('?');
  if (!query || path !== pathname) return false;
  const sp = new URLSearchParams(search);
  for (const q of [query, ...also]) {
    const [key, value] = q.split('=');
    const now = sp.get(key);
    if (now != null && now === value) return true;
  }
  const [key] = query.split('=');
  if (sp.get(key) != null) return false;
  return DEFAULT_QUERY[path] === query;
}

function resolveActive(pathname, navGroups = NAV_GROUPS) {
  const all = [...PINNED, ...navGroups.flatMap((g) => flatItems(g.items))];
  let best = null;
  for (const it of all) {
    if (pathname === it.to || pathname.startsWith(`${it.to}/`)) {
      if (!best || it.to.length > best.length) best = it.to;
    }
  }
  return best;
}

// Trimmed to a bare direction arrow - the arrow itself carries the meaning
// (down-left = inbound, up-right = outbound), so it reads without a text label;
// aria-label keeps it announced for screen readers.
function DirectionTag({ dir }) {
  const inbound = dir === 'inbound';
  const Icon = inbound ? ArrowDownLeft : ArrowUpRight;
  return (
    <Icon
      className={cn('size-3.5 shrink-0', inbound ? 'text-blue-600' : 'text-amber-600')}
      strokeWidth={2.5}
      aria-label={inbound ? 'Inbound' : 'Outbound'}
    />
  );
}

function NavLink({ item, collapsed, active, badgeValue, primary }) {
  const Icon = item.icon;
  const badge = badgeValue != null ? badgeValue : item.badge;

  const link = (
    <Link
      to={item.to}
      onMouseEnter={(e) => preloadRoute(item.to, e)}
      onFocus={(e) => preloadRoute(item.to, e)}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group flex h-9 items-center gap-2.5 rounded-md px-3 text-[13px] font-medium leading-5 transition-colors duration-fast',
        'text-ink-900 hover:bg-surface-subtle hover:text-foreground dark:text-ink-100',
        // Top-level rows (Command Center) sit beside the section doors, so they
        // carry the same visual weight as the doors - not the weight of a leaf
        // item inside a section.
        primary && 'text-[13px] font-medium text-foreground',
        active && 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent',
        collapsed && 'justify-center px-0'
      )}
    >
      <Icon
        className={cn(
          'size-4 shrink-0',
          active ? 'text-sidebar-accent-foreground' : 'text-ink-600 group-hover:text-foreground'
        )}
        strokeWidth={1.75}
      />
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.tag && <DirectionTag dir={item.tag} />}
          {badge != null && Number(badge) > 0 && (
            <Badge variant="default" className="px-1.5 py-0 text-[12px] tabular">
              {badge}
            </Badge>
          )}
        </>
      )}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

/* ── The sub-page panel ──────────────────────────────────────────────────────
 * Four pages in the whole product have a level beneath them: Analytics &
 * Insights (10 reports), Recommendations (8 rules), Audience performance and
 * Funnels. They used to unfold as a nested list inside the rail, which only
 * appeared once you were already standing in the page - so the one moment you
 * needed to see the choices, on your way in, was the one moment they were
 * hidden.
 *
 * They open as a panel instead. The rail is 250px and these rows carry a
 * sentence each (INSIGHT_TABS.d, RULE_SECTIONS.why) plus, for the rules, a live
 * state. None of that ever fitted in the rail and all of it is what the person
 * opening the menu came to find out.
 *
 * Because only four rows open, the chevron means something. A row without one
 * navigates and nothing else.
 */
function SubPanel({ item, activeTo, dyn, onClose, anchorRef }) {
  const ref = useRef(null);
  const loc = useLocation();
  /* Rendered in a portal, not in the rail.
   *
   * The rail scrolls, so it clips: an absolutely positioned panel inside it is
   * cut off at the rail's edge and simply never appears. Position is therefore
   * computed from the anchor's viewport rect and the panel is fixed to the
   * viewport, which also lets it flip upward near the bottom of a short window
   * instead of running off the screen. */
  const [pos, setPos] = useState(null);
  useEffect(() => {
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      // Clear the RAIL's edge, not the chevron's: the chevron sits a few pixels
      // inside the rail, so anchoring to it tucked the panel under the sidebar.
      const aside = anchorRef.current.closest('aside');
      const railRight = aside?.getBoundingClientRect().right ?? a.right;
      const left = Math.max(a.right, railRight) + 16;
      const W = Math.min(544, window.innerWidth - left - 16);
      /* The floor is the top of the rail's Collapse band, with a gap, so a
         panel opened from the last row never sits on that band's border (it
         used to end 8px below the row, right on the line). Without a band
         (collapsed or missing) it only stops the panel touching the window.
         The caret is clamped to the panel, so a higher panel still joins the
         row through the bridge line. */
      const GAP = 16;
      const footTop = aside?.querySelector('[data-rail-foot]')?.getBoundingClientRect().top;
      const floor = Math.min(window.innerHeight, footTop ?? Infinity) - GAP;
      const room = floor - 8;
      const H = Math.min(ref.current?.offsetHeight || 340, room);
      /* Below the row when it fits, otherwise UP from the row's bottom edge -
         the way a native menu flips - so one edge of the panel always sits on
         the row that opened it. Then clamped to the floor. */
      const below = a.top - 8;
      const fitsBelow = below + H <= floor;
      const top = Math.max(8, Math.min(fitsBelow ? below : a.bottom - H, floor - H));
      /* The join. The caret aims at the row's centre; when the panel had to
         sit higher than that, the caret stops at the panel's bottom corner and
         the bridge line leaves the row at the caret's height instead, so the
         two always meet. */
      const mid = a.top + a.height / 2;
      const caretY = Math.max(12, Math.min(mid - top - 7.5, H - 27));
      const joinY = top + caretY + 7.5;
      setPos({ left, top, width: Math.max(300, W), maxHeight: room, caretY, bridge: { x: railRight, y: joinY, w: left - railRight } });
    };
    place();
    // The first pass runs before the panel has been laid out, so its height is
    // a guess and the flip-up used the wrong number - on a short window the
    // panel ran off the bottom. Re-place whenever its real size settles.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    if (ro && ref.current) ro.observe(ref.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    /* The row moves without any of those firing: opening Settings collapses
       the other groups and the rail re-flows, so the row climbed 50px while
       the panel and its caret stayed where the row HAD been - the join drawn
       to empty space. Watch the anchor's rect each frame while open (one
       getBoundingClientRect, no layout work) and re-place on any change. */
    let last = '';
    let raf = 0;
    const watch = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      const key = a ? `${a.top}|${a.left}|${a.height}` : '';
      if (key !== last) { last = key; place(); }
      raf = requestAnimationFrame(watch);
    };
    raf = requestAnimationFrame(watch);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { onClose(); anchorRef.current?.focus(); } };
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [onClose, anchorRef]);

  // Arrow keys walk the panel; Home/End jump the ends. The rows are links, so
  // Enter is the browser's.
  const onKeyDown = (e) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const rows = [...(ref.current?.querySelectorAll('[data-panel-row]') || [])];
    if (!rows.length) return;
    const i = rows.indexOf(document.activeElement);
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? rows.length - 1
        : e.key === 'ArrowDown' ? (i + 1 + rows.length) % rows.length
          : (i - 1 + rows.length) % rows.length;
    rows[next]?.focus();
  };

  // A wrapper child ("Recommendation rules") exists only to hold the real rows;
  // the panel is that level, so it is skipped rather than drawn as a row that
  // opens another row.
  const rows = (item.children || []).flatMap((c) => (c.children?.length ? c.children : [c]));
  // The caption ("3 of 8 rules live") is declared on the wrapper we just
  // flattened through, so read it from there as well as from the item - it is
  // the one number someone opens this menu to check.
  const capKey = item.captionKey || (item.children || []).find((c) => c.captionKey)?.captionKey;
  const caption = capKey ? dyn?.[capKey]?.caption : null;
  const PIcon = item.icon;

  return createPortal((
    <div
      ref={ref}
      role="menu"
      aria-label={item.label}
      onKeyDown={onKeyDown}
      style={pos ? { left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight } : { opacity: 0 }}
      className="fixed z-50 flex flex-col rounded-xl border border-border bg-popover shadow-[0_16px_40px_rgba(20,32,27,.15),0_2px_6px_rgba(20,32,27,.07)] motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-150"
    >
      {/* The join. A 2px line runs from the row's edge across the gap into a
          caret on the panel's border, both at the row's vertical centre, so the
          eye reads "this panel belongs to that row" without a caption. Drawn
          as siblings of the panel body, outside its own scroll. */}
      {pos && (
        <>
          <span aria-hidden="true" className="pointer-events-none fixed z-50 h-[2px] rounded-full bg-primary"
            style={{ left: pos.bridge.x, top: pos.bridge.y - 1, width: pos.bridge.w }} />
          <span aria-hidden="true" className="pointer-events-none absolute -left-[8px] size-[15px] rotate-45 rounded-[2px] border-b-2 border-l-2 border-primary bg-popover"
            style={{ top: pos.caretY }} />
        </>
      )}
      {/* The row no longer navigates, so the section's OWN page would have been
          orphaned. The header is that page. */}
      <Link
        to={item.to}
        data-panel-row=""
        onClick={onClose}
        className="flex shrink-0 items-center gap-2 rounded-t-[11px] border-b border-border/60 bg-muted/40 px-3.5 py-2.5 transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {PIcon && <PIcon className="size-4 shrink-0 text-primary" strokeWidth={1.75} />}
        <span className="text-[13.5px] font-semibold tracking-tight">{item.label}</span>
        <span className="ml-auto text-[11.5px] text-muted-foreground">
          {caption || `${rows.length} ${rows.length === 1 ? 'page' : 'pages'}`}
        </span>
      </Link>
      <div className={cn('grid min-h-0 flex-1 gap-0 overflow-y-auto rounded-b-[11px] p-1.5', rows.some((c) => c.desc) && 'sm:grid-cols-2')}>
        {rows.map((c) => {
          const CIcon = c.icon;
          const on = c.to === activeTo || queryChildActive(c.to, loc.pathname, loc.search, c.also);
          const live = c.liveKey && dyn?.recsLive?.map ? !!dyn.recsLive.map[c.liveKey] : null;
          return (
            <Link
              key={c.to}
              to={c.to}
              data-panel-row=""
              role="menuitem"
              onClick={onClose}
              onMouseEnter={(e) => preloadRoute(c.to, e)}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'flex items-start gap-2.5 rounded-lg px-2.5 py-2 transition-colors duration-fast',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                on ? 'bg-primary/10' : 'hover:bg-surface-subtle'
              )}
            >
              {c.n != null
                ? <span className="mt-0.5 w-4 shrink-0 text-center text-[11px] font-bold tabular text-muted-foreground">{c.n}</span>
                : CIcon && <CIcon className={cn('mt-0.5 size-4 shrink-0', on ? 'text-primary' : 'text-ink-600')} strokeWidth={1.75} />}
              <span className="min-w-0 flex-1">
                <span className={cn('block text-[13.5px] font-semibold leading-tight', on && 'text-primary')}>{c.label}</span>
                {c.desc && <span className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-muted-foreground" title={c.desc}>{c.desc}</span>}
              </span>
              {live != null && (
                <span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', live ? 'bg-emerald-500' : 'bg-muted-foreground/30')}
                  role="img" aria-label={live ? 'Live' : 'Not set'} title={live ? 'Live' : 'Not set'} />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  ), document.body);
}

// A child row on the rail. Every row draws the icon it has always declared in
// NAV_GROUPS and never rendered - without it the rail was 68 identical lines of
// text with nothing for the eye to hold, which is the whole reason it read as
// filler rather than a product.
function RailLink({ item, active, badgeValue, depth = 0, activeTo, dynamicBadges, dyn }) {
  // Some sub-rows are a QUERY on the parent's page, not a path of their own, so
  // the highlight has to read the search string too. Through the router, not
  // window.location: a lens change is a query change and only the router
  // re-renders us for one.
  const loc = useLocation();
  const badge = badgeValue != null ? badgeValue : item.badge;
  const trail = !active && inTrail(item, activeTo);
  // Sub-menu extras: a caption under the label ("1 of 8 rules live") and a
  // status dot per row, both fed by the sidebar so the rows never fetch.
  const caption = item.captionKey ? dyn?.[item.captionKey]?.caption : null;
  const liveState = item.liveKey && dyn?.recsLive?.map ? !!dyn.recsLive.map[item.liveKey] : null;
  // A row's own state ({ tone, word }): Server GTM says Live, Stopped or Set up.
  const status = item.statusKey ? dyn?.[item.statusKey] : null;
  const RIcon = item.icon;
  // Only a row with real sub-pages opens a panel. Four rows in the product do,
  // which is what makes the chevron worth reading: everything else navigates.
  const hasPanel = Array.isArray(item.children) && item.children.length > 0 && depth === 0;
  const [panel, setPanel] = useState(false);
  const chevRef = useRef(null);
  return (
    <div className={cn(hasPanel && 'relative')}>
      <Link
        ref={chevRef}
        to={item.to}
        onMouseEnter={(e) => preloadRoute(item.to, e)}
        // A row with sub-pages opens them. It used to navigate and only the
        // chevron opened - which meant the choices were one extra click behind
        // the very click someone made to go looking for them. The section's own
        // page is not lost: it is the first thing in the panel.
        onClick={hasPanel ? (e) => { e.preventDefault(); setPanel((v) => !v); } : undefined}
        aria-expanded={hasPanel ? panel : undefined}
        aria-haspopup={hasPanel ? 'menu' : undefined}
        aria-current={active ? 'page' : undefined}
        className={cn(
          // Page links read light in Lato: its 400 has thinner strokes than
          // Inter's, and they were also sitting on the MUTED ink. Lato ships no
          // 500 or 600, so the step up cannot come from weight without jumping
          // to 700 and shouting over the section headers. Near-full ink plus
          // half a pixel does the same job quietly.
          // Inter at 500 in full ink (owner review, 29 Sep 2026: the rail read
          // as washed-out grey at 400). Active steps up to 600 and brand.
          'group/row relative flex items-center gap-2 rounded-md py-[5px] pl-3 pr-2.5 text-[13px] font-medium transition-colors duration-fast',
          depth > 0 && 'py-[4px] pl-3 text-[13px]',
          // The chevron is an inline child now, so it takes its own space -
          // reserving padding for it as well cost ~20px and truncated
          // "Analytics & Insights" to "Analytics & Insig...".
          hasPanel && 'pr-1.5',
          // Lit while its panel is open, in the same ink as the bridge line
          // running out of it, so the row and the panel read as one thing.
          hasPanel && panel && 'bg-primary/10 text-primary ring-1 ring-primary/40',
          active
            ? 'bg-primary/10 font-semibold text-primary'
            : trail
              // The page you are standing inside, while one of its sub-pages is the active one.
              ? 'font-medium text-foreground hover:bg-surface-subtle'
              : 'text-ink-950 hover:bg-surface-subtle hover:text-foreground dark:text-ink-100'
        )}
      >
        {active && (
          <span aria-hidden="true"
            className="absolute -left-px bottom-1 top-1 w-[2px] rounded-full bg-primary" />
        )}
        {item.n != null && <span className="w-3.5 shrink-0 text-right text-[12px] tabular text-muted-foreground">{item.n}.</span>}
        {/* The fixed icon column. Every label starts on one vertical line
            whether or not its row has an icon, so the column reads as a column
            rather than as ragged indentation. */}
        {item.n == null && (
          RIcon
            ? <RIcon className={cn('size-4 shrink-0', active ? 'text-primary' : 'text-ink-700 group-hover/row:text-foreground dark:text-ink-300')} strokeWidth={1.75} />
            : <span className="size-4 shrink-0" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1">
          {/* A deep row keeps its whole name: at two levels of indent a
              truncated "Bigger size, better ..." says nothing. */}
          <span className={cn('block', depth > 1 ? 'leading-tight' : 'truncate')}>{item.label}</span>
          {caption && <span className="block truncate text-[12px] font-normal leading-4 text-muted-foreground">{caption}</span>}
        </span>
        {liveState != null && (
          <span className={cn('size-1.5 shrink-0 rounded-full', liveState ? 'bg-emerald-500' : 'bg-muted-foreground/40')}
            role="img" aria-label={liveState ? 'Live' : 'Not set'} title={liveState ? 'Live' : 'Not set'} />
        )}
        {status && (status.tone === 'setup' ? (
          <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-px text-[11px] font-semibold text-primary">{status.word}</span>
        ) : (
          <span className={cn('size-1.5 shrink-0 rounded-full', status.tone === 'good' ? 'bg-emerald-500' : 'bg-amber-500')}
            role="img" aria-label={status.word} title={status.word} />
        ))}
        {item.tag && <DirectionTag dir={item.tag} />}
        {badge != null && Number(badge) > 0 && (
          <Badge variant="default" className="px-1.5 py-0 text-[12px] tabular">{badge}</Badge>
        )}
        {/* Not a control any more, just the sign that this row opens rather
            than goes. Only four rows in the product carry one. */}
        {hasPanel && (
          <ChevronRight className={cn('size-3.5 shrink-0 text-ink-700 transition-transform duration-200 motion-reduce:transition-none', panel && 'rotate-90')} aria-hidden="true" />
        )}
      </Link>
      {hasPanel && panel && (
        <SubPanel item={item} activeTo={activeTo} dyn={dyn} anchorRef={chevRef} onClose={() => setPanel(false)} />
      )}
      {/* ONCE YOU ARE INSIDE THE SECTION, ITS PAGES STAY IN THE RAIL.
       *
       * The panel is how you choose on the way in. It is not navigation: it
       * closes the moment you pick, and then you are standing in one report
       * with no sign that nine others exist. Somebody reading Channels could
       * not discover Retention without knowing to come back and click the
       * parent again.
       *
       * So the section's pages live in the rail for as long as you are in the
       * section, which is what Google Cloud does and what the old nested list
       * did before the panel replaced it. The panel gained the descriptions;
       * it should not have taken the standing list with it. */}
      {hasPanel && inTrail(item, activeTo) && (
        <div className="mb-0.5 ml-3 flex flex-col border-l border-sidebar-border motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200">
          {(item.children || []).flatMap((c) => (c.children?.length ? c.children : [c])).map((c) => (
            <RailLink key={c.to} item={c} depth={depth + 1}
              active={c.to === activeTo || queryChildActive(c.to, loc.pathname, loc.search, c.also)}
              activeTo={activeTo} dynamicBadges={dynamicBadges} dyn={dyn} />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupSection({ group, open, onToggle, activeTo, hasActiveChild, dynamicBadges, dyn }) {
  const regionId = `nav-grp-${group.key}`;
  return (
    <div>
      {/* The reference's header: the NAME is the header - bold, chevron at the
          far right pointing down when open. The group icon goes; a bold label
          does the job an icon plus label was doing, in less noise. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={regionId}
        className="group flex w-full items-center gap-1.5 rounded-md px-2 py-[4px] text-[13.5px] font-semibold tracking-tight text-foreground transition-colors hover:bg-sidebar-accent/40"
      >
        <span className="flex-1 truncate text-left">{group.label}</span>
        {!open && hasActiveChild && (
          <span className="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
        )}
        {!open && (
          <span className="text-[12px] tabular text-foreground/90">{group.items.length}</span>
        )}
        <ChevronRight
          className={cn(
            'size-4 shrink-0 text-foreground/90 transition-transform duration-200 group-hover:text-foreground motion-reduce:transition-none',
            open && 'rotate-90'
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          id={regionId}
          className="ml-3 mb-0.5 flex flex-col border-l border-sidebar-border motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200"
        >
          {group.items.map((item) => (
            <RailLink
              key={item.to}
              item={item}
              active={item.to === activeTo}
              activeTo={activeTo}
              dynamicBadges={dynamicBadges}
              dyn={dyn}
              badgeValue={item.badgeKey ? dynamicBadges[item.badgeKey] : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();

  // WhatsApp badge = number of conversations with an UNREAD inbound message
  // (an inbound newer than when you last opened it). This matches the green
  // dots in the inbox exactly. Recompute when the local read-state changes.
  const { data: waThreads = [] } = useWhatsAppThreads(false);
  const [readTick, setReadTick] = useState(0);
  useEffect(() => {
    const h = () => setReadTick((t) => t + 1);
    window.addEventListener(READ_CHANGE_EVENT, h);
    return () => window.removeEventListener(READ_CHANGE_EVENT, h);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const waUnread = useMemo(() => countUnread(waThreads), [waThreads, readTick]);
  const { data: coldCount = 0 } = usePartialCaptureCount();
  const dynamicBadges = { whatsapp: waUnread, coldCaptures: coldCount };

  // Workspaces can hide whole feature modules (a reversible display choice):
  // scheduling -> Calendar/Meetings, students -> the Academy group.
  const { enabled: schedulingOn } = useScheduling();
  const { enabled: studentsOn } = useStudents();
  const { enabled: ecomOn } = useEcommerce();
  const { enabled: leadsOn } = useLeadDesk();
  const { enabled: dealsOn } = useDeals();
  // Upsells & bumps shows where order lines carry a role (SamCart stores).
  const { data: upsellsAvail } = useQuery({
    queryKey: ['upsells', 'available'],
    queryFn: async () => (await api.get('/api/commerce/insights/upsells/available')).data,
    enabled: !!ecomOn, staleTime: 30 * 60_000, retry: false,
  });
  const upsellsOn = !!upsellsAvail?.available;

  // The Recommendations sub-menu says how many rules are live and marks each
  // row. Fetched only while standing inside Recommendations, on the rules
  // page's own query keys, so it is one request shared with the page.
  const inRecs = location.pathname.startsWith('/commerce/recommendations');
  const recs = useRecsRules({ enabled: ecomOn && inRecs });
  // The server container's state for its row, on the page's own query key so
  // the page and the rail share one request; read lazily, never polled here.
  const { data: sc } = useQuery({
    queryKey: ['server-container'],
    queryFn: async () => (await api.get('/api/server-container')).data,
    staleTime: 5 * 60_000, retry: false,
  });
  const scState = sc?.container ? sc.container.state : null;
  const dyn = {
    recsLive: { map: recs.live, caption: recs.liveCount != null ? `${recs.liveCount} of ${recs.total} rules live` : null },
    sgtm: !sc ? null
      : !sc.container ? { tone: 'setup', word: 'Set up' }
        : ['running', 'live'].includes(scState) ? { tone: 'good', word: 'Live' }
          : { tone: 'warn', word: scState ? `Not live: ${scState}` : 'Not live' },
  };

  // Pinned + groups, filtered to the items this user may open (per
  // lib/permissions) minus any module this workspace turned off. Leaf paths
  // are unchanged from before, so saved permissions still resolve.
  // The new reports (lib/reports-v2) bring their own menu, with the front
  // door as the first row of Home rather than pinned above everything.
  const { available: v2Available, on: v2, setView } = useReportsV2();
  const NAV = v2 ? NAV_GROUPS_V2 : NAV_GROUPS;
  // A focused workspace (a Server GTM hosting signup) sees Server GTM, the
  // cards, and the sections of the cards it switched on (lib/workspace-
  // modules; the shell sends every other address to Server GTM).
  const { focus, modules } = useWorkspaceFocus();
  const focused = !!focus;
  const modulesKey = modules.join(',');
  const pinned = focused ? [SERVER_GTM_ITEM, ADD_FEATURES_ITEM] : (v2 ? [] : PINNED).filter((i) => allowedPaths().has(i.to))
    // In store mode the landing page IS the Command Center - one page, one
    // name, one door. "Dashboard" was a placeholder word; the front door
    // deserves the product's own name.
    .map((i) => (i.to === '/dashboard' && ecomOn ? { ...i, label: 'Command Center', icon: Gauge } : i));
  const groups = useMemo(() => {
    const allow = allowedPaths();
    const on = modulesKey ? modulesKey.split(',') : [];
    return NAV
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) =>
            [].concat(i.gate || i.to).some((g) => allow.has(g)) &&
            inWorkspace(i, { schedulingOn, studentsOn, ecomOn, leadsOn, dealsOn, upsellsOn })
        ).map((i) => (ecomOn && i.ecomLabel ? { ...i, label: i.ecomLabel, icon: i.ecomIcon || i.icon } : i))
          .map((i) => (Array.isArray(i.children)
          // Sub-menu rows obey the same gates: a settings tab a role cannot
          // open, or one that only a store (or only a lead desk) has, is not listed.
          ? { ...i, children: i.children.filter((c) => [].concat(c.gate || String(c.to).split('?')[0]).some((g) => allow.has(g)) && (ecomOn ? !c.ecomHide : !c.ecomOnly)) }
          : i))
          // Focused: only the switched-on cards' sections; Server GTM is pinned.
          .filter((i) => !focused || (i.to !== SERVER_GTM_ITEM.to && openInFocus(i.to, on))),
      }))
      .filter((g) => g.items.length > 0);
  }, [NAV, focused, modulesKey, schedulingOn, studentsOn, ecomOn, leadsOn, dealsOn, upsellsOn]);

  // Workspace name for the strip below the logo. The cached value is only
  // written when an agency member ENTERS the workspace, so renaming the
  // workspace in Settings used to leave the old name here until they switched
  // workspaces again. /me carries the live name (same query key as the shell's
  // banner, so this shares that fetch), and we write it back so the cache
  // self-heals for anything else still reading it.
  const { data: me } = useQuery({
    queryKey: ['auth', 'me-shell'],
    queryFn: async () => (await api.get('/api/auth/me')).data,
    staleTime: 5 * 60_000,
    retry: false,
    enabled: inMemberSession(),
  });
  // The NAME is rendered in the top bar now; this only keeps the shared cache
  // honest for anything still reading it synchronously.
  useEffect(() => {
    if (me?.org_name) setCurrentWorkspaceName(me.org_name);
  }, [me?.org_name]);

  const activeTo = useMemo(() => resolveActive(location.pathname, NAV), [location.pathname, NAV]);
  const activeGroupKey = useMemo(
    () => NAV.find((g) => flatItems(g.items).some((it) => it.to === activeTo))?.key || null,
    [activeTo, NAV]
  );

  // Open/closed state, remembered per browser.
  const [openMap, setOpenMap] = useState(() => ({ ...DEFAULT_OPEN, ...(loadGroupState() || {}) }));
  useEffect(() => {
    try {
      localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(openMap));
    } catch {}
  }, [openMap]);

  // Active-trail: when you move INTO a different group, open it. Manual
  // toggles inside the group you're already on are left alone (prev starts
  // null so a deep-link / fresh load still reveals the active group once).
  const prevGroup = useRef(null);
  useEffect(() => {
    if (activeGroupKey && activeGroupKey !== prevGroup.current) {
      setOpenMap((m) => (m[activeGroupKey] ? m : { ...m, [activeGroupKey]: true }));
    }
    prevGroup.current = activeGroupKey;
  }, [activeGroupKey]);

  const toggle = (key) => setOpenMap((m) => ({ ...m, [key]: !m[key] }));

  return (
    <TooltipProvider delayDuration={150}>
      <aside
        className={cn(
          // No right border. The content panel curves away from the rail
          // instead, which is the join Google Cloud draws and the one a hard
          // 1px line was standing in for.
          'flex h-full flex-col bg-sidebar font-nav transition-[width] duration-normal ease-standard',
          collapsed ? 'w-[64px]' : 'w-[232px]'
        )}
      >
        {/* Brand */}
        <div
          className={cn(
            'flex h-14 items-center px-3',
            collapsed && 'justify-center px-0'
          )}
        >
          <Link
            to="/dashboard"
            className="flex min-w-0 items-center gap-1 truncate"
            aria-label="Cookiebees - Home"
          >
            {/* Inline mark, not <img src="/favicon.svg">: the app is served under
                /app/, so that root-absolute path 301s to the SPA shell and the
                browser gets HTML where it wanted an image - which is why this
                rendered as a broken-image icon for every tenant. */}
            {collapsed ? (
              <MMLogoMark size={32} />
            ) : (
              <>
                {/* The bee plus the actual wordmark - the name used to be set in
                    the UI font, which is not the logo, just the name typed out. */}
                <MMLogoMark size={32} />
                <CookieBeesWordmark className="h-[19px] w-auto shrink-0 text-foreground" />
              </>
            )}
          </Link>
        </div>

        {/* The workspace name moved to the top bar, beside the brand.
            Here it needed a divider of its own, and that divider ran to the
            rail's right edge where the content's curve begins 16px lower - so
            the two could never meet and the join read as a broken line. Up
            there it spans the whole screen, which is what it governs. */}

        {/* Nav */}
        <nav className="rail-scroll flex-1 overflow-y-auto px-2 py-2">
          {collapsed ? (
            // Icon rail: pinned + every allowed leaf as an icon (tooltip names
            // it), with a hairline divider between groups. Headers/tags can't
            // fit at 60px, so the tree only shows when expanded.
            <div className="flex flex-col gap-0.5">
              {v2 && !focused && (
                <button type="button" onClick={openFindReport} aria-label="Find a report" title="Find a report"
                  className="flex h-9 items-center justify-center rounded-md text-ink-600 hover:bg-surface-subtle hover:text-foreground">
                  <Search className="size-4" strokeWidth={1.75} />
                </button>
              )}
              {pinned.map((item) => (
                <NavLink key={item.to} item={item} collapsed active={item.to === activeTo} />
              ))}
              {groups.map((group) => (
                <React.Fragment key={group.key}>
                  <div className="my-1 border-t border-sidebar-border/60" />
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      item={item}
                      collapsed
                      active={item.to === activeTo}
                      badgeValue={item.badgeKey ? dynamicBadges[item.badgeKey] : undefined}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <>
              {/* The full map, always: Command Center pinned, then EVERY
                  section with its pages inline - no doors, no back button.
                  The design language stays: one divided column of connected
                  rows, bold group names with a chevron, rail-and-accent
                  children. Collapsing a group is allowed; starting hidden
                  is not. */}
              {v2 && !focused && (
                /* Find a report: the search that replaces "Where to look".
                   It answers from the report index (lib/report-index), so it
                   cannot go stale the way a hand-written guide did. */
                <button type="button" onClick={openFindReport}
                  className="mb-2 flex w-full items-center gap-2 rounded-md border border-sidebar-border bg-background px-2.5 py-[7px] text-left text-[13px] text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground">
                  <Search className="size-3.5 shrink-0" />
                  <span className="flex-1 truncate">Find a report</span>
                  <kbd className="rounded border border-border bg-muted px-1 font-sans text-[11px] text-muted-foreground">/</kbd>
                </button>
              )}
              {!focused && <SetupNudge />}
              <div className="divide-y divide-sidebar-border">
                {pinned.map((item) => {
                  const PIcon = item.icon;
                  const active = item.to === activeTo;
                  return (
                    <Link key={item.to} to={item.to} aria-current={active ? 'page' : undefined}
                      className={cn('flex items-center gap-2.5 rounded-md px-2 py-[7px] text-[14px] font-medium transition-colors duration-fast',
                        active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-foreground hover:bg-surface-subtle')}>
                      <span className={cn('grid size-7 shrink-0 place-items-center rounded-lg transition-colors',
                        active ? 'bg-primary text-primary-foreground' : 'bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300')}>
                        <PIcon className="size-4" strokeWidth={2} />
                      </span>
                      <span className="flex-1 truncate">{item.label}</span>
                    </Link>
                  );
                })}
                {groups.map((group) => (
                  <div key={group.key} className="py-0.5">
                    <GroupSection
                      group={group}
                      open={!!openMap[group.key]}
                      onToggle={() => toggle(group.key)}
                      activeTo={activeTo}
                      hasActiveChild={group.key === activeGroupKey}
                      dynamicBadges={dynamicBadges}
                      dyn={dyn}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* Collapse toggle */}
        <div data-rail-foot className="border-t border-sidebar-border p-2">
          {v2Available && !focused && !collapsed && (
            /* New reports or the current ones, per person. Both stay complete:
               switching changes the menu and where old links land, never a
               number. */
            <div className="mb-1.5 flex items-center gap-2 px-1">
              <span className="text-[12px] text-muted-foreground">Reports</span>
              <div className="ml-auto inline-flex rounded-md border border-sidebar-border bg-background p-0.5" role="group" aria-label="Reports view">
                {[['new', 'New'], ['current', 'Current']].map(([v, l]) => {
                  const on = (v === 'new') === v2;
                  return (
                    <button key={v} type="button" aria-pressed={on} onClick={() => setView(v)}
                      className={cn('rounded px-2 py-0.5 text-[12px] font-medium transition-colors',
                        on ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground')}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <button
            onClick={onToggle}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-sidebar-accent/40 hover:text-foreground',
              collapsed && 'justify-center'
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronsRight className="size-4" />
            ) : (
              <>
                <ChevronsLeft className="size-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
